import {patchState, signalStore, withComputed, withHooks, withLinkedState, withMethods, withProps, withState} from "@ngrx/signals";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {AutosaveConfig} from "../../../../../../shared/shared-components/autosave-widget/autosave-widget.component";
import {
	MultiRoomRecordingSession,
	MultiRoomRecordingStation,
	Scenario,
	SimUser,
	TrainingRoom,
	UpsertMultiRoomRecordingSessionMetaDataRequest,
} from "@models";
import {NotificationService, SimEventService, SimSessionService} from "@services";
import {computed, DestroyRef, effect, inject, linkedSignal} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {debounceTime, filter} from "rxjs/operators";
import {distinctUntilChanged} from "rxjs";
import {Translations} from "@translations";
import {TranslateService} from "@ngx-translate/core";
import {DatePipe} from "@angular/common";
import {SimTimeService} from "../../../../../../core/services/sim-services/sim-time.service";
import {toLazySignal} from "ngxtension/to-lazy-signal";
import {explicitEffect} from "ngxtension/explicit-effect";

export type MultiRoomRecordingSessionDialogMetadataForm = FormGroup<{
	title: FormControl<string | null>;
	date: FormControl<Date | null>;
	startTime: FormControl<string | null>;
	participants: FormControl<SimUser[] | null>;
}>;

export type MultiRoomRecordingSessionDialogStationForm = FormGroup<{
	room: FormControl<TrainingRoom | null>;
	scenario: FormControl<Scenario | null>;
}>;

export type MultiRoomRecordingSessionDialogState = {
	eventId: string;
	autosaveConfig: AutosaveConfig;
	session: MultiRoomRecordingSession | null;
	stationIndex: number | null;
	isFormCooldown: boolean;
	isLoading: boolean;
};

export const MultiRoomRecordingSessionDialogStore = signalStore(
	withState<MultiRoomRecordingSessionDialogState>(() => ({
		autosaveConfig: {},
		session: null,
		stationIndex: null,
		eventId: "",
		isFormCooldown: false,
		isLoading: true,
	})),
	withProps(() => ({
		sessionService: inject(SimSessionService),
		eventService: inject(SimEventService),
		notificationService: inject(NotificationService),
		destroyRef: inject(DestroyRef),
		translate: inject(TranslateService),
		datePipe: inject(DatePipe),
		metadataForm: new FormGroup({
			title: new FormControl<string | null>(null, [Validators.required]),
			date: new FormControl<Date | null>(null, [Validators.required]),
			startTime: new FormControl<string | null>(null, [Validators.required]),
			participants: new FormControl<SimUser[]>([]),
		}),
		stationForms: new FormArray<MultiRoomRecordingSessionDialogStationForm>([]),
	})),
	// Form
	withComputed((store) => ({
		currentStationForm: computed<MultiRoomRecordingSessionDialogStationForm | null>(
			() => store.stationForms.controls.at(store.stationIndex() ?? 0) ?? null,
		),
		stationValueChanges: toLazySignal(store.stationForms.valueChanges),
		metadataValueChanges: toLazySignal(store.metadataForm.valueChanges),
	})),
	// Exclude IDs
	withComputed((store) => ({
		excludeRoomIds: computed<string[]>(
			() =>
				store
					.stationValueChanges()
					?.filter((x) => !!x.room?.id)
					.map((p) => p.room!.id) ?? [],
		),
	})),
	withLinkedState((store) => ({
		viewStations: linkedSignal<Partial<MultiRoomRecordingStation>[]>(() => store.session()?.stations ?? []),
	})),
	// Private methods
	withMethods((store) => ({
		_populateForms(session: MultiRoomRecordingSession) {
			const date = new Date(session.startTime);
			const startTime = store.datePipe.transform(new Date(session.startTime), store.translate.instant(Translations.DATE_FORMAT_TIME));
			store.metadataForm.setValue(
				{
					title: session.title,
					date,
					startTime,
					participants: session.participants.map((p) => p.user),
				},
				{emitEvent: false},
			);
			session.stations.forEach((station) => {
				store.stationForms.push(
					new FormGroup({
						room: new FormControl<TrainingRoom | null>(station.room ?? null, [Validators.required]),
						scenario: new FormControl<Scenario | null>(station.scenario ?? null, [Validators.required]),
					}),
					{emitEvent: false},
				);
			});
		},
		async _upsertMetadata(value: MultiRoomRecordingSessionDialogMetadataForm["value"]) {
			const sessionId = store.session()?.id;
			const request = {
				sessionId,
				eventId: store.eventId(),
				title: value.title,
				date: value.date,
				participants: value.participants,
				startTime: SimTimeService.addTimeStringToDate(value.date!, value.startTime!),
			} as UpsertMultiRoomRecordingSessionMetaDataRequest;
			const promise = store.sessionService.upsertMultiRoomRecordingSessionMetaData(request);
			patchState(store, {autosaveConfig: {...store.autosaveConfig(), promise}});
			patchState(store, {session: await promise});

			// Assign session to event (if freshly created)
			if (!sessionId) {
				await store.eventService.addSessionToEvent(store.eventId(), store.session()!.id);
			}
		},
		async _upsertStation() {
			const stationIndex = store.stationIndex();
			if (stationIndex === null) {
				return;
			}
			const value = store.stationForms.at(stationIndex).getRawValue();
			const sessionId = store.session()?.id;
			const roomId = value.room?.id;
			const scenarioId = value.scenario?.id;
			if (!sessionId || !roomId || !scenarioId) {
				throw new Error();
			}

			const stationId = store.session()?.stations.at(stationIndex)?.id;
			const promise = stationId
				? store.sessionService.updateStationInMultiRoomRecordingSession({
						stationId,
						sessionId,
						roomId,
						scenarioId,
					})
				: store.sessionService.addStationToMultiRoomRecordingSession({
						sessionId,
						roomId,
						scenarioId,
					});
			patchState(store, {autosaveConfig: {...store.autosaveConfig(), promise}});
			patchState(store, {session: await promise});
		},
	})),
	// Public methods
	withMethods((store) => ({
		async storeInitialized(eventDay: string | Date, eventId: string, sessionId?: string) {
			patchState(store, {eventId});
			const eventDate = new Date(eventDay);
			if (eventDate) {
				store.metadataForm.controls.date.setValue(eventDate);
			}
			if (!sessionId) {
				patchState(store, {isLoading: false});
				return;
			}
			const session = (await store.sessionService.getSessionById(sessionId)) as MultiRoomRecordingSession;
			store._populateForms(session);
			patchState(store, {session, isLoading: false});

			// Select first station per default
			if (session.stations.length) {
				patchState(store, {stationIndex: 0});
			}
		},
		participantAdded(participant: SimUser) {
			const rawValue = store.metadataForm.controls.participants.getRawValue() ?? [];
			store.metadataForm.controls.participants.setValue([...new Set([...rawValue, participant])]);
		},
		participantRemoved(userId: string) {
			const rawValue = store.metadataForm.controls.participants.getRawValue() ?? [];
			store.metadataForm.controls.participants.setValue(rawValue.filter((p) => p.id !== userId));
		},
		stationAdded() {
			store.stationForms.push(
				new FormGroup({
					room: new FormControl<TrainingRoom | null>(null, [Validators.required]),
					scenario: new FormControl<Scenario | null>(null, [Validators.required]),
				}),
			);
			const viewStations = store.viewStations();
			patchState(store, {stationIndex: store.stationForms.length - 1, viewStations: [...viewStations, {id: "new"}]});
		},
		async stationDeleted(stationIndex: number) {
			const stationId = store.viewStations().at(stationIndex)?.id;
			try {
				if (stationId && stationId !== "new") {
					const session = await store.sessionService.removeStationFromMultiRoomRecordingSession({
						stationId,
						sessionId: store.session()!.id,
					});
					patchState(store, {session});
				} else {
					patchState(store, {viewStations: store.viewStations().filter((x) => x.id !== stationId)});
				}
				store.stationForms.removeAt(stationIndex, {emitEvent: false});
				if (store.stationIndex() === stationIndex) {
					const newIndex = store.stationForms.length ? Math.max(0, stationIndex - 1) : null;
					patchState(store, {stationIndex: newIndex});
				}
			} catch {
				store.notificationService.notifyError();
			}
		},
		stationSelected(stationIndex: number) {
			patchState(store, {stationIndex});
		},
	})),
	withHooks((store) => ({
		onInit() {
			// Subscriptions
			store.metadataForm.valueChanges
				.pipe(
					takeUntilDestroyed(store.destroyRef),
					filter(() => store.metadataForm.valid),
					debounceTime(500),
					distinctUntilChanged(isMetadataDistinct),
				)
				.subscribe(store._upsertMetadata);
			store.stationForms.valueChanges
				.pipe(
					takeUntilDestroyed(store.destroyRef),
					filter(() => store.stationForms.valid),
					debounceTime(500),
				)
				.subscribe(store._upsertStation);

			// Station change side effects
			explicitEffect([store.stationIndex], async () => {
				patchState(store, {isFormCooldown: true});
				setTimeout(() => patchState(store, {isFormCooldown: false}));
			});
			effect(() => {
				if (store.stationForms.length && !store.currentStationForm()) {
					patchState(store, {stationIndex: 0});
				}
			});
		},
		onDestroy() {
			store.metadataForm.reset();
			store.stationForms.clear();
		},
	})),
);

function isMetadataDistinct(
	valueA: MultiRoomRecordingSessionDialogMetadataForm["value"],
	valueB: MultiRoomRecordingSessionDialogMetadataForm["value"],
): boolean {
	return !(
		valueA.title === valueB.title ||
		valueA.date?.getTime() === valueB.date?.getTime() ||
		valueA.startTime === valueB.startTime ||
		valueA.participants?.length === valueB.participants?.length ||
		valueA.participants?.some((participant, index) => participant.id === valueB.participants?.at(index)?.id)
	);
}
